import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { OMS_COLORS, sectionVariants, tint } from '../omsDashTheme';
import type { ExecutiveInsight, InsightTone } from '../utils/deriveCommand';
import type { OmsDrillBucket } from '../types';

interface Props {
  insights: ExecutiveInsight[];
  loading: boolean;
  onOpenProject: (projectId: string) => void;
  onDrill: (bucket: OmsDrillBucket) => void;
  /** Focus the Explorer's search on an entity (e.g. an overloaded engineer). */
  onFindInExplorer: (name: string) => void;
}

const TONE: Record<InsightTone, { ink: string; fill: string; label: string }> = {
  critical: { ink: '#9F1239', fill: '#9F1239', label: 'Act today' },
  warning: { ink: '#8A5A08', fill: '#C9A961', label: 'Watch' },
  neutral: { ink: '#5A6275', fill: '#5A6275', label: 'Next step' },
  positive: { ink: '#1F5A47', fill: '#339274', label: 'Working' },
};

/** Verb CTAs naming outcomes, per insight kind. */
function ctaOf(insight: ExecutiveInsight): string {
  switch (insight.id) {
    case 'riskiest-project':
    case 'most-delayed':
      return 'Open project';
    case 'overloaded-engineer':
      return 'Rebalance workload';
    case 'idle-resources':
      return 'Review inactive engineers';
    case 'ready-to-send-review':
      return 'Send drafts for review';
    case 'pm-compliance':
      return 'Review maintenance plan';
    default:
      return 'Investigate';
  }
}

/**
 * Today's focus — at most three computed triage cards: severity, one
 * sentence, one verb CTA. The severity ladder here is the page's only
 * alarm system; nothing below the fold repeats it.
 */
export default function TodayFocus({ insights, loading, onOpenProject, onDrill, onFindInExplorer }: Props) {
  if (loading && insights.length === 0) {
    return (
      <section aria-label="Today's focus">
        <div className="grid gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-[104px] animate-pulse rounded-2xl bg-[rgba(11,59,46,0.05)]" />
          ))}
        </div>
      </section>
    );
  }

  const triage = insights.filter((i) => i.tone !== 'positive').slice(0, 3);
  if (triage.length === 0) {
    return (
      <motion.section variants={sectionVariants} initial="hidden" animate="visible" custom={2} aria-label="Today's focus" className="px-2">
        <p className="text-sm font-semibold" style={{ color: '#1F5A47' }}>
          All clear — nothing needs your attention right now. The Explorer below has the full picture.
        </p>
      </motion.section>
    );
  }

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={2}
      aria-label="Today's focus"
    >
      <div className="grid gap-3 md:grid-cols-3">
        {triage.map((insight, index) => {
          const tone = TONE[insight.tone];
          const act = () => {
            if (insight.projectId) onOpenProject(insight.projectId);
            else if (insight.id === 'overloaded-engineer') onFindInExplorer(insight.title);
            else if (insight.bucket) onDrill(insight.bucket);
          };
          const clickable = Boolean(insight.projectId || insight.bucket || insight.id === 'overloaded-engineer');
          return (
            <motion.article
              key={insight.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 + index * 0.07, duration: 0.35 }}
              className="relative rounded-2xl border p-3.5"
              style={{
                backgroundColor: 'rgba(250,247,238,0.96)',
                borderColor: insight.tone === 'critical' ? 'rgba(159,18,57,0.35)' : 'rgba(11,59,46,0.22)',
              }}
            >
              <p className="flex items-center gap-1.5">
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em]"
                  style={{ color: tone.ink, backgroundColor: tint(tone.fill, 0.14) }}
                >
                  {tone.label}
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[0.16em]" style={{ color: OMS_COLORS.caption }}>
                  {insight.eyebrow}
                </span>
              </p>
              <h3 className="mt-1.5 truncate text-[14px] font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                {insight.title}
              </h3>
              <p className="mt-0.5 line-clamp-2 text-[11.5px] font-medium leading-snug" style={{ color: OMS_COLORS.slate }}>
                {insight.detail}
              </p>
              {clickable && (
                <button
                  type="button"
                  onClick={act}
                  className="mt-2 flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.08em] transition hover:bg-[rgba(11,59,46,0.06)]"
                  style={{ color: '#1F5A47', border: '1px solid rgba(11,59,46,0.22)', backgroundColor: '#fcfaf3' }}
                >
                  {ctaOf(insight)} <ArrowUpRight className="h-3 w-3" />
                </button>
              )}
            </motion.article>
          );
        })}
      </div>
    </motion.section>
  );
}
